import {
  Children,
  type FC,
  isValidElement,
  type PropsWithChildren,
  type ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Box, Skeleton, type SxProps, type Theme, useMediaQuery, useRadioGroup } from '@mui/material'
import {
  H6,
  Body2,
  Body1,
  useIsMobileVariant,
  ListItem,
  ListItemSelectedIcon,
  ListItemSelect,
  Radio,
  ListItemIllustration,
  type RadioProps,
  Paper,
} from '@dhl-ui/components'
import { breakpoints } from '@dhl/theme'

export type FlexibleListItemRadioProps = Pick<RadioProps, 'disabled' | 'onChange' | 'value'>

export const flexibleListItemTemplateAreas: Record<string, string> = {
  default: '"illustration header" "illustration content"',
  narrow: '"illustration" "header"',
  wide: '"illustration header" "content content"',
}

export type FlexibleListItemProps = FlexibleListItemRadioProps &
  PropsWithChildren<{
    ['data-testid']?: string
    illustration?: ReactNode
    subheader?: ReactNode
    title?: ReactNode
    gridTemplateAreas?: keyof typeof flexibleListItemTemplateAreas
  }>

export const flexibleListItemClasses = {
  illustration: 'DhlFlexibleListItem-illustration',
  content: 'DhlFlexibleListItem-content',
}

const defaultInnerStyles: SxProps<Theme> = {
  gridTemplateAreas: flexibleListItemTemplateAreas.default,
  gridAutoColumns: 'auto 1fr',
  gridAutoRows: 'auto 1fr',
}

const narrowStyles: SxProps<Theme> = {
  paddingTop: (theme) => theme.spacing(1),
  gridTemplateAreas: flexibleListItemTemplateAreas.narrow,
  gridAutoColumns: '1fr',
  justifyItems: 'center',
  textAlign: 'center',
  [`& .${flexibleListItemClasses.illustration}`]: {
    width: 80,
  },
  [`& .${flexibleListItemClasses.content}`]: {
    display: 'none',
  },
}

const wideStyles: SxProps<Theme> = {
  gridTemplateAreas: flexibleListItemTemplateAreas.wide,
  gridAutoColumns: 'auto 1fr',
  justifyItems: 'initial',
  textAlign: 'initial',
  [`& .${flexibleListItemClasses.content}`]: {
    display: 'block',
  },
}

const styles: Record<string, SxProps<Theme>> = {
  root: {
    position: 'relative',
    display: 'flex',
    height: '100%',
    '.DhlListItem-item': {
      height: '100%',
    },
  },
  inner: {
    display: 'grid',
    width: '100%',
    rowGap: 1,
  },
  illustration: {
    gridArea: 'illustration',
    alignSelf: 'center',
    height: 'auto',
    marginRight: 1,
    [breakpoints.down('lg')]: {
      width: 80,
    },
    [breakpoints.up('lg')]: {
      marginRight: 0,
    },
  },
  header: {
    gridArea: 'header',
  },
  content: {
    gridArea: 'content',
  },
}

type FlexibleListItemContainerProps = PropsWithChildren & {
  ['data-testid']?: string
  fullWidth?: boolean
}

export const FlexibleListItemContainer: FC<FlexibleListItemContainerProps> = ({
  children,
  fullWidth = false,
  ...rest
}) => {
  const childCount = Children.toArray(children).filter((child) => isValidElement(child)).length

  if (!childCount) {
    return null
  }

  return (
    <Box
      {...rest}
      display="grid"
      gridTemplateColumns={{ sm: 'repeat(2, 1fr)', xs: !fullWidth && childCount === 2 ? 'repeat(2, 1fr)' : '1fr' }}
      gap={2}
    >
      {children}
    </Box>
  )
}

export const FlexibleListItem: FC<FlexibleListItemProps> = ({
  children,
  gridTemplateAreas,
  disabled,
  illustration: IllustrationComponent,
  subheader,
  title,
  'data-testid': dataTestId,
  ...radioProps
}) => {
  const radioGroup = useRadioGroup()
  const ref = useRef<HTMLDivElement>(null)
  const [isNarrow, setIsNarrow] = useState(false)
  const showHeader = !!title || !!subheader
  const testId = (id = '') => [dataTestId, id].filter(Boolean).join('-')
  const isXs = useMediaQuery((theme) => theme.breakpoints.down('sm'))

  const checked = radioGroup?.value === radioProps.value

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) {
      return undefined
    }

    // Measure synchronously before paint to avoid a flicker on the first render.
    setIsNarrow(el.getBoundingClientRect().width < window.innerWidth * 0.5)

    const observer = new ResizeObserver(([entry]) => {
      setIsNarrow(entry.contentRect.width < window.innerWidth * 0.5)
    })
    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  const innerStyles = useMemo(() => {
    if (!isXs) {
      return defaultInnerStyles
    }

    return isNarrow ? narrowStyles : wideStyles
  }, [isNarrow, isXs])

  return (
    <Paper isRounded data-testid={testId()} isOutlined={!checked} sx={{ height: '100%' }}>
      <Box data-testid={testId('root')} position="relative" sx={styles.root}>
        <ListItem
          isButton
          isRounded
          isDisabled={disabled}
          ListComponent="div"
          hasDivider={false}
          isSelected={checked}
          variant="selectable"
        >
          <ListItemSelect isHidden>
            <Radio
              aria-disabled={disabled}
              checked={checked}
              data-testid={testId('radio')}
              disabled={disabled}
              {...radioProps}
            />
          </ListItemSelect>
          <Box
            ref={ref}
            sx={
              {
                ...styles.inner,
                ...innerStyles,
                ...(gridTemplateAreas ? { gridTemplateAreas: flexibleListItemTemplateAreas[gridTemplateAreas] } : {}),
              } as SxProps<Theme>
            }
          >
            <Box
              data-testid={testId('illustration')}
              className={flexibleListItemClasses.illustration}
              sx={styles.illustration}
            >
              <ListItemIllustration>{IllustrationComponent}</ListItemIllustration>
            </Box>

            {showHeader && (
              <Box sx={styles.header}>
                {title && (
                  <H6 data-testid={testId('title')} component="div">
                    {title}
                  </H6>
                )}
                {subheader && (
                  <Body2 data-testid={testId('subheader')} variant="secondary" component="div">
                    {subheader}
                  </Body2>
                )}
              </Box>
            )}

            {children && (
              <Box data-testid={testId('content')} className={flexibleListItemClasses.content} sx={styles.content}>
                <Body1 component="div">{children}</Body1>
              </Box>
            )}
          </Box>
        </ListItem>

        {checked && (
          <Box
            data-testid={testId('checked')}
            sx={(theme) => ({
              position: 'absolute',
              top: theme.spacing(2),
              right: theme.spacing(2),
              svg: { height: 24, width: 24 },
            })}
          >
            <ListItemSelectedIcon />
          </Box>
        )}
      </Box>
    </Paper>
  )
}

type FlexibleListItemSkeletonProps = FlexibleListItemProps & {
  slotProps?: {
    showSubheader?: boolean
  }
}

export const FlexibleListItemSkeleton: FC<FlexibleListItemSkeletonProps> = ({ children, slotProps, ...rest }) => {
  const isMobile = useIsMobileVariant()

  return (
    <FlexibleListItem
      {...rest}
      illustration={<Skeleton variant="rounded" height={60} width={isMobile ? 60 : 80} />}
      title={<Skeleton variant="text" width={isMobile ? 100 : 200} />}
      subheader={(slotProps?.showSubheader ?? true) && <Skeleton variant="text" width={80} />}
    >
      {children}
    </FlexibleListItem>
  )
}
